import { ReactElement } from 'react';
import { useLocation } from 'contexts/LocationContext';
import {
  Aircon,
  Droplet,
  ElectricVehicle,
  HouseDark,
  Insulation,
  Sun
} from 'svg';
import { Location } from 'types';
import { dateFormat } from 'utils/date';
import { NumberStyleType, formatNumber } from 'utils/number';

export type SpotlightTypes =
  | 'Sun'
  | 'HotWaterHeatPump'
  | 'ElectricVehicle'
  | 'AirCon'
  | 'Insulation'
  | 'Scheme';

export type CalculationType = 'percentageSettled';
export interface SpotlightDetails {
  title: string;
  richTitle?: ReactElement;
  description: string;
  icon: JSX.Element;
  format?: NumberStyleType;
  field?: keyof Location;
  calculationType?: CalculationType;
}

export interface Spotlight {
  spotlightType: SpotlightTypes;
  suburb: string;
  numOfSettled: number;
  location: Location;
}

export interface Props {
  spotlights: Spotlight[];
  lastUpdatedAt: string;
}

export const spotlightsMap: Record<SpotlightTypes, SpotlightDetails> = {
  Scheme: {
    title: 'Highest overall uptake',
    description: 'in zero interest loans accessed.',
    icon: <HouseDark />,
    format: 'currency',
    field: 'amountSettled'
  },
  HotWaterHeatPump: {
    title: 'Singing in the shower',
    description: 'of installs in [suburb] are hot water heat pumps.',
    icon: <Droplet />,
    format: 'percent',
    field: 'numSettledHotWaterHeatPumpsHwhp',
    calculationType: 'percentageSettled'
  },
  ElectricVehicle: {
    title: 'Driving into the future',
    description: 'of products in [suburb] are electric vehicles.',
    icon: <ElectricVehicle />,
    format: 'percent',
    field: 'numSettledAllElectricVehicles',
    calculationType: 'percentageSettled'
  },
  AirCon: {
    title: 'Staying warm and keeping cool',
    description: 'of installs in [suburb] are reverse cycle air conditioners.',
    icon: <Aircon />,
    format: 'percent',
    field: 'numSettledElectricHeatingAndCoolingSystemsReverseCycle',
    calculationType: 'percentageSettled'
  },
  Insulation: {
    title: 'Comfort in the home',
    description: 'of installs in [suburb] are for insulation.',
    icon: <Insulation />,
    format: 'percent',
    field: 'numSettledInsulation',
    calculationType: 'percentageSettled'
  },
  Sun: {
    title: 'Most solar uptake',
    description: 'of installs in [suburb] includes solar systems.',
    icon: <Sun />,
    format: 'percent',
    field: 'numSettledAllSolar',
    calculationType: 'percentageSettled'
  }
};

function getDescription(description: string, suburb: string) {
  return description.replace(/\[suburb\]/g, suburb);
}

export default function SuburbSpotlight({ spotlights, lastUpdatedAt }: Props) {
  const [, setLocation] = useLocation();

  return (
    <div id="suburb-spotlight">
      <h2>Suburb Spotlight</h2>
      <div className="mt-4" id="suburb-spotlight-sub">
        <p>
          The leading Canberra suburbs who have accessed finance as part of the
          Sustainable Household Scheme across each category. Last updated
          on&nbsp;
          {dateFormat(lastUpdatedAt)}.
        </p>
      </div>
      <div className="section suburb-spotlight-cards">
        {spotlights.map((spotlight) => (
          <div
            className="suburb-spotlight-card gap-2"
            key={spotlight.spotlightType}
          >
            <div className="suburb-spotlight-icon">
              {spotlightsMap[spotlight.spotlightType].icon}
            </div>
            <div className="suburb-spotlight-details">
              <h3 aria-label={spotlightsMap[spotlight.spotlightType].title}>
                {spotlightsMap[spotlight.spotlightType].richTitle ||
                  spotlightsMap[spotlight.spotlightType].title}
              </h3>
              {/* Web Display */}
              <a
                aria-label={`See details for ${spotlight.suburb}`}
                className="h3 link-text hidden sm:block"
                onClick={() => {
                  document.location.href = '#';
                  document.location.href = '#act-dashboard-google-map';
                  setLocation(spotlight.location);
                }}
                href="#act-dashboard-google-map"
              >
                {spotlight.suburb}
              </a>
              {/* Mobile Display */}
              <a
                aria-label={`See details for ${spotlight.suburb}`}
                className="h3 link-text sm:hidden"
                onClick={() => {
                  document.location.href = '#';
                  document.location.href = '#act-dashboard-google-map';
                  setLocation(spotlight.location);
                }}
                href="#act-dashboard-google-map"
              >
                {spotlight.suburb}
              </a>
              <p className="mt-4">
                {formatNumber(
                  spotlight.numOfSettled,
                  spotlightsMap[spotlight.spotlightType].format
                )}
                &nbsp;
                {getDescription(
                  spotlightsMap[spotlight.spotlightType].description,
                  spotlight.suburb
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
